<template>
    <Teleport to="body">
        <Transition name="fade">
            <div
                v-if="props.modelValue"
                class="fixed inset-0 z-[999] flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
                aria-modal="true"
                role="dialog"
                tabindex="0"
                @click.self="handleBackdrop"
                @keydown.esc="handleEsc"
            >
                <div
                    class="absolute inset-0 bg-black/40"
                    @click="handleBackdrop"
                />

                <div
                    ref="panelRef"
                    class="relative w-full max-w-[640px] rounded-[12.8px] border border-gray-200 bg-white px-4 py-6 sm:px-[21px] sm:py-[42px] shadow-xl modal-panel focus:outline-none focus:ring-0 focus:ring-offset-0 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-3rem)] overflow-y-auto"
                    tabindex="-1"
                    @click.stop
                >
                    <slot name="panel-decorations" />

                    <div
                        class="flex flex-col items-center gap-[17.5px] text-center modal-stack"
                    >
                        <img
                            v-if="props.imgSrc && !props.imgRaw"
                            :src="props.imgSrc"
                            :alt="props.imgAlt"
                            class="block mx-auto max-w-full h-auto modal-img"
                            :class="props.imgClass"
                            :style="imgStyle"
                        />

                        <div
                            v-else-if="props.imgRaw"
                            class="block mx-auto max-w-full h-auto modal-img"
                            :class="props.imgClass"
                            :style="imgStyle"
                            v-html="props.imgRaw"
                        />
                        <div
                            class="flex w-full flex-col items-center gap-[41px]"
                        >
                            <div class="w-full">
                                <h2
                                    class="font-inter font-semibold text-[21px] leading-[1.42] modal-title"
                                >
                                    {{ props.title }}
                                </h2>

                                <div
                                    class="mx-auto mt-2 max-w-[370px] px-0 sm:px-4 leading-[1.6] modal-body"
                                    :class="props.bodyClass"
                                    :style="bodyStyle"
                                >
                                    <slot name="body">
                                        <p
                                            v-if="
                                                props.bodyText ||
                                                props.emphasisText
                                            "
                                        >
                                            {{ props.bodyText
                                            }}<template
                                                v-if="
                                                    props.bodyText &&
                                                    props.emphasisText
                                                "
                                                >,&nbsp;</template
                                            ><span
                                                v-if="props.emphasisText"
                                                class="font-semibold text-gray-700"
                                                >{{ props.emphasisText }}
                                            </span>

                                            <template v-if="props.showDot"
                                                >.</template
                                            >
                                        </p>
                                    </slot>
                                </div>
                            </div>

                            <div
                                class="flex flex-wrap items-center justify-center gap-3 modal-actions"
                            >
                                <slot name="actions">
                                    <template
                                        v-for="a in props.actions"
                                        :key="a.id"
                                    >
                                        <component
                                            :is="getLinkComponent(a)"
                                            v-if="a.href"
                                            :href="a.href"
                                            class="cursor-pointer"
                                            @click.prevent="handleAction(a)"
                                        >
                                            <AtomicButton
                                                :color="a.color ?? 'yellow'"
                                                :size="a.size ?? 'lg'"
                                            >
                                                {{ a.label }}
                                            </AtomicButton>
                                        </component>

                                        <AtomicButton
                                            v-else
                                            :color="a.color ?? 'yellow'"
                                            :size="a.size ?? 'lg'"
                                            @click="handleAction(a)"
                                        >
                                            {{ a.label }}
                                        </AtomicButton>
                                    </template>
                                </slot>
                            </div>

                            <button
                                v-if="props.cancelLabel && !hasCancelInActions"
                                type="button"
                                class="text-sm text-gray-500 hover:text-gray-700"
                                @click="close"
                            >
                                {{ props.cancelLabel }}
                            </button>
                        </div>
                    </div>

                    <!-- optional corner close -->
                    <button
                        v-if="props.showCloseX"
                        type="button"
                        class="absolute right-4 top-4 rounded p-2 text-gray-400 hover:text-gray-600"
                        aria-label="Close"
                        @click="close"
                    >
                        ✕
                    </button>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";

type ButtonColor = "yellow" | "blue" | "gray" | string;
type ButtonSize = "sm" | "md" | "lg" | string;

type PopupAction = {
    id: string;
    label: string;
    color?: ButtonColor;
    size?: ButtonSize;

    href?: string;
    useInertiaLink?: boolean;

    close?: boolean;
};

const props = withDefaults(
    defineProps<{
        modelValue: boolean;

        imgSrc?: string;
        imgRaw?: string;
        imgClass?: string;
        imgAlt?: string;

        title: string;

        bodyText?: string;
        emphasisText?: string;
        showDot?: boolean;

        actions?: PopupAction[];

        closeOnBackdrop?: boolean;
        closeOnEsc?: boolean;

        cancelLabel?: string;
        showCloseX?: boolean;

        customWidth?: number;
        customHeight?: number;
        bodyClass?: string;
        bodyMaxWidth?: number | string;
    }>(),
    {
        imgAlt: "",
        showDot: true,
        actions: () => [],
        closeOnBackdrop: true,
        closeOnEsc: true,
        cancelLabel: "",
        showCloseX: false,
        customWidth: 375,
        customHeight: 259,
        bodyClass: "",
        bodyMaxWidth: undefined,
    },
);

const emit = defineEmits<{
    (e: "update:modelValue", v: boolean): void;
    (e: "close"): void;
    (e: "action", id: string): void;
}>();

const panelRef = ref<HTMLElement | null>(null);

const imgStyle = computed(() => ({
    width: "min(100%, 375px)",
    maxHeight: "40vh",
}));
const bodyStyle = computed(() => {
    if (props.bodyMaxWidth === undefined) {
        return undefined;
    }

    return {
        maxWidth:
            typeof props.bodyMaxWidth === "number"
                ? `${props.bodyMaxWidth}px`
                : props.bodyMaxWidth,
    };
});
const hasCancelInActions = computed(() =>
    (props.actions ?? []).some((a) => a.id === "cancel"),
);

function close() {
    emit("update:modelValue", false);
    emit("close");
}

function handleBackdrop() {
    if (props.closeOnBackdrop) close();
}

function handleEsc() {
    if (props.closeOnEsc) close();
}

function handleAction(a: PopupAction) {
    emit("action", a.id);
    if (a.close) close();
}

function getLinkComponent(a: PopupAction) {
    return (a.useInertiaLink ?? true) ? "inertia-link" : "a";
}

watch(
    () => props.modelValue,
    async (open) => {
        if (open) {
            await nextTick();
            panelRef.value?.focus?.();
        }
    },
);

onMounted(() => {
    if (props.modelValue) panelRef.value?.focus?.();
});
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 150ms ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

@media (max-height: 663px) {
    .modal-panel {
        margin-top: 42px;
    }
}

@media (max-height: 580px) {
    .modal-panel {
        padding: 14px !important;
        max-height: calc(100vh - 16px);
        margin-top: 50px;
    }

    .modal-stack {
        gap: 12px;
    }

    .modal-title {
        font-size: 16px;
        line-height: 1.35;
    }

    .modal-body {
        margin-top: 6px;
        font-size: 14px;
        line-height: 1.5;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .modal-img {
        max-height: 26vh;
    }

    .modal-actions {
        gap: 8px;
    }
}

@media (max-height: 440px) {
    .modal-panel {
        margin-top: 42px;
        padding: 14px !important;
        max-height: calc(100vh - 32px);
    }

    .modal-stack {
        gap: 12px;
    }

    .modal-title {
        font-size: 12px;
        line-height: 1.35;
    }

    .modal-body {
        margin-top: 6px;
        font-size: 10px;
        line-height: 1.5;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .modal-img {
        max-height: 22vh;
    }

    .modal-actions {
        gap: 8px;
    }
}

@media (max-height: 400px) {
    .modal-panel {
        margin-top: 42px;
        padding: 4px !important;
        max-height: calc(100vh - 66px);
    }

    .modal-stack {
        gap: 4px;
    }

    .modal-title {
        font-size: 10px;
        line-height: 1.35;
    }

    .modal-body {
        margin-top: 6px;
        font-size: 8px;
        line-height: 1.5;
        padding-left: 0 !important;
        padding-right: 0 !important;
    }

    .modal-img {
        max-height: 12vh !important;
    }

    .modal-actions {
        gap: 4px;
    }

    :deep(.modal-actions :is(button, a, [role="button"])) {
        width: 75px !important;
        height: 35px !important;
        font-size: 10px !important;
        line-height: 1 !important;
        box-sizing: border-box !important;
    }
}

@media (max-height: 200px) {
    .modal-img {
        display: none;
    }
}

.modal-img :deep(svg) {
    display: block;
    width: 100%;
    height: auto;
    max-height: 40vh;
}
</style>
